import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { ElephantPose } from "./illustration/ElephantPose";

/**
 * 送った・提出したあとに、移った先の画面で一度だけ出す紙ひこうきのゾウ（PRODUCT_DEV-1201）。
 *
 * 日程調整の送信・日報の提出は、終わるとすぐ別の画面へ移る。送った画面で出しても1フレームも
 * 描画されない（同じコミットでアンマウントされる）ので、`navigate(…, { state: { sentNotice } })` で
 * 遷移先へ持ち越し、外枠（RootChrome）のここで出す。SavedMeetingToast と同じやり方。
 *
 * 画面の操作は止めない（下の画面に覆いを掛けない）。カードを押すか、少し待つと消える。
 */

/** 履歴で同じ画面に戻ったときに出し直さないよう、送った直後だけ出す。 */
const FRESH_MS = 15_000;
/** 紙ひこうきが鼻先から出て飛んでいくまで（約 2 秒）と、文言を読める長さ */
const SHOW_MS = 2_600;
const FADE_MS = 200;
/**
 * 出した印の sentAt を覚えておく場所。location.state は履歴に残るので、送ってから 15 秒以内に
 * 再読み込みすると、画面の state（lastSentAt）が消えてもう一度出てしまう。タブを閉じれば消えてよい。
 * 使えない環境（プライベートブラウズ等）では覚えずに進む（二度出ることがあるだけで、壊れはしない）
 */
const SHOWN_KEY = "unizo.sentCelebration.lastSentAt";

const readShownAt = (): number | null => {
  try {
    const v = window.sessionStorage.getItem(SHOWN_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
};

const rememberShownAt = (sentAt: number): void => {
  try {
    window.sessionStorage.setItem(SHOWN_KEY, String(sentAt));
  } catch {
    // 覚えられなくても表示は続ける
  }
};

export interface SentNotice {
  sentAt: number;
  message: string;
}

/** 遷移先へ渡す state を作る（呼び出し側で Date.now() を書かずに済ませる） */
export const sentNoticeState = (message: string): { sentNotice: SentNotice } => ({
  sentNotice: { sentAt: Date.now(), message },
});

export function SentCelebration() {
  const location = useLocation();
  const notice = (location.state as { sentNotice?: SentNotice } | null)?.sentNotice;
  const [shown, setShown] = useState<SentNotice | null>(null);
  const [lastSentAt, setLastSentAt] = useState<number | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const sentAt = notice?.sentAt;
    if (typeof sentAt !== "number" || sentAt === lastSentAt) return;
    if (Date.now() - sentAt > FRESH_MS || readShownAt() === sentAt) return;
    rememberShownAt(sentAt);
    setLastSentAt(sentAt);
    setLeaving(false);
    setShown({ sentAt, message: notice?.message ?? "送信しました" });
  }, [notice?.sentAt, notice?.message, lastSentAt]);

  const close = useCallback(() => setLeaving(true), []);

  useEffect(() => {
    if (!shown) return;
    const id = window.setTimeout(close, SHOW_MS);
    return () => window.clearTimeout(id);
  }, [shown, close]);

  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(() => setShown(null), FADE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]);

  if (!shown) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[28%] z-50 flex justify-center px-4">
      <button
        key={shown.sentAt}
        type="button"
        role="status"
        aria-live="polite"
        onClick={close}
        className={`pointer-events-auto flex flex-col items-center rounded-2xl bg-white px-8 pb-5 pt-4 shadow-card ${
          leaving ? "animate-fadeOut" : "animate-fadeIn"
        }`}
      >
        <ElephantPose pose="sent" width={150} />
        <span className="mt-2 text-base font-bold text-neutral-900">{shown.message}</span>
      </button>
    </div>
  );
}
